import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { adminGet } from "../services/api";
import {
  ArrowExportRegular, ServerRegular, DatabaseRegular, FlashRegular, CloudRegular, MoneyRegular, PulseRegular,
  DeveloperBoardRegular, HardDriveRegular, CheckmarkCircleRegular, PauseCircleRegular, NewsRegular,
} from "@fluentui/react-icons";
import { fluentIcon } from "../lib/fluentIcon";

const ExternalLink = fluentIcon(ArrowExportRegular);
const Server = fluentIcon(ServerRegular);
const Database = fluentIcon(DatabaseRegular);
const Zap = fluentIcon(FlashRegular);
const Cloud = fluentIcon(CloudRegular);
const DollarSign = fluentIcon(MoneyRegular);
const Activity = fluentIcon(PulseRegular);
const Cpu = fluentIcon(DeveloperBoardRegular);
const HardDrive = fluentIcon(HardDriveRegular);
const CheckCircle = fluentIcon(CheckmarkCircleRegular);
const MinusCircle = fluentIcon(PauseCircleRegular);
const Newspaper = fluentIcon(NewsRegular);

interface GCPService {
  name: string;
  type: string;
  cpu: string;
  memory: string;
  minInstances: number;
  maxInstances: number;
  region: string;
  status: string;
  estimatedCost: number;
}

interface APIUsage {
  plan: string;
  limit: string;
  used_24h: number;
  cost: number;
  notes: string;
}

interface NewsSource {
  name: string;
  slug: string;
  type: string;
  url: string;
  language: string;
  reliability_score: number;
  total_articles: number;
  articles_24h: number;
  articles_7d: number;
  latest_article: string | null;
}

interface CostStats {
  estimated_monthly: Record<string, number>;
  volume_last_24h: {
    ai_calls: number;
    ai_tokens: number;
    articles_ingested: number;
  };
  api_usage: Record<string, APIUsage>;
  news_sources: NewsSource[];
  services: GCPService[];
  database: {
    total_articles: number;
    total_profiles: number;
    total_insights: number;
    disk_size_gb: number;
    version: string;
    tier: string;
  };
}

export default function CostPage() {
  const { token } = useAuth();
  const { data, isLoading, error, dataUpdatedAt } = useQuery<CostStats>({
    queryKey: ["cost-stats"],
    queryFn: () => adminGet<CostStats>("/api/v1/admin/stats/cost", token),
    refetchInterval: 60_000,
  });

  const updatedSecsAgo = dataUpdatedAt ? Math.max(0, Math.round((Date.now() - dataUpdatedAt) / 1000)) : null;
  const updatedLabel =
    updatedSecsAgo === null ? "" : updatedSecsAgo < 5 ? "just now" : updatedSecsAgo < 60 ? `${updatedSecsAgo}s ago` : `${Math.round(updatedSecsAgo / 60)}m ago`;

  // GCP billing console (real spend lives here; the figures below are estimates)
  const BILLING_CONSOLE_URL = "https://console.cloud.google.com/billing/01BECC-690BFE-769846/reports?project=tomitra-prod";

  // Group the monthly breakdown so it reads by category instead of one flat list.
  const COST_GROUPS: { title: string; keys: (k: string) => boolean }[] = [
    { title: "Infrastructure (GCP)", keys: (k) => k.startsWith("cloud_run") || k.startsWith("cloud_sql") || k === "artifact_registry" || k === "cloud_storage" },
    { title: "AI & Data APIs", keys: (k) => k === "gemini_api" || k === "gnews_api" },
    { title: "Firebase & OTA", keys: (k) => k.startsWith("firebase") || k === "eas_updates" },
    { title: "App Stores & Domain", keys: (k) => k === "google_play_console" || k === "apple_developer" || k.startsWith("domain") },
  ];
  const prettyKey = (k: string) =>
    k.replace(/_/g, " ").replace(/\bapi\b/gi, "API").replace(/\bgcp\b/gi, "GCP").replace(/^./, (c) => c.toUpperCase());

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Cost & Resources</h1>
        <p className="text-sm text-slate-500 mt-1">Live GCP resource status with cost estimates and usage volume.</p>
      </div>

      <div className="flex items-start gap-2 mb-6 bg-slate-900/40 border border-slate-800 rounded-lg px-4 py-2.5">
        <DollarSign size={13} className="text-slate-500 mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Dollar figures are <span className="text-slate-400">derived estimates</span> (resources &times; rate), not billed amounts. The Cloud Run table is read live from GCP; other lines are amortized fixed costs.
          {" "}
          <a href={BILLING_CONSOLE_URL} target="_blank" rel="noreferrer" className="text-brand-blue hover:underline inline-flex items-center gap-0.5">
            View actual bill <ExternalLink size={10} />
          </a>
        </p>
      </div>

      {isLoading && <div className="text-slate-500">Loading...</div>}
      {error && <div className="bg-red-950/30 border border-red-900/40 rounded-xl p-4 text-red-400 text-sm">{(error as Error).message}</div>}

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <StatCard icon={<DollarSign size={16} />} label="Est. Monthly" value={`$${data.estimated_monthly.total?.toFixed(0)}`} sub="All services" color="text-emerald-400" />
            <StatCard icon={<Zap size={16} />} label="AI Calls (24h)" value={data.volume_last_24h.ai_calls.toLocaleString()} sub={`${data.volume_last_24h.ai_tokens.toLocaleString()} tokens`} />
            <StatCard icon={<Activity size={16} />} label="Articles (24h)" value={data.volume_last_24h.articles_ingested.toLocaleString()} sub="Ingested" />
            <StatCard icon={<Database size={16} />} label="Total Articles" value={data.database.total_articles.toLocaleString()} sub={`${data.database.total_profiles} users`} />
          </div>

          {/* GCP Services */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Cloud size={16} className="text-brand-blue" />
              <h3 className="text-sm font-semibold text-slate-300">GCP Cloud Run Services</h3>
              <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2 py-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
              </span>
              <span className="text-[10px] text-slate-500 ml-auto">
                Region: asia-south1 (Mumbai){updatedLabel && <span className="text-slate-600"> &middot; updated {updatedLabel}</span>}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">Service</th>
                    <th className="text-left py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">Type</th>
                    <th className="text-left py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">CPU</th>
                    <th className="text-left py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">Memory</th>
                    <th className="text-left py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">Instances</th>
                    <th className="text-left py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">Status</th>
                    <th className="text-right py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">$/mo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.services.map(s => (
                    <tr key={s.name} className="border-b border-slate-800/50 hover:bg-slate-800/20">
                      <td className="py-2.5 px-3">
                        <div className="text-slate-200 font-medium">{s.name}</div>
                      </td>
                      <td className="py-2.5 px-3 text-slate-400">{s.type}</td>
                      <td className="py-2.5 px-3 text-slate-400">{s.cpu}</td>
                      <td className="py-2.5 px-3 text-slate-400">{s.memory}</td>
                      <td className="py-2.5 px-3 text-slate-400">{s.minInstances}-{s.maxInstances}</td>
                      <td className="py-2.5 px-3">
                        {s.status === "active" ? (
                          <span className="flex items-center gap-1 text-emerald-400"><CheckCircle size={10} /> Active</span>
                        ) : (
                          <span className="flex items-center gap-1 text-slate-500"><MinusCircle size={10} /> Idle</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-slate-300">${s.estimatedCost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Database + Cost breakdown side by side */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Database size={16} className="text-emerald-400" />
                <h3 className="text-sm font-semibold text-slate-300">Cloud SQL Database</h3>
              </div>
              <div className="space-y-3">
                {[
                  ["Engine", data.database.version],
                  ["Tier", data.database.tier],
                  ["Disk", `${data.database.disk_size_gb} GB SSD`],
                  ["Articles", data.database.total_articles.toLocaleString()],
                  ["Users", data.database.total_profiles.toString()],
                  ["Insights Cached", data.database.total_insights.toLocaleString()],
                  ["Backups", "Daily (22:00 UTC)"],
                  ["Region", "asia-south1 (Mumbai)"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between py-1 border-b border-slate-800/50">
                    <span className="text-xs text-slate-500">{k}</span>
                    <span className="text-xs text-slate-300 font-mono">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign size={16} className="text-yellow-400" />
                <h3 className="text-sm font-semibold text-slate-300">Monthly Cost Breakdown</h3>
              </div>
              <div className="space-y-4">
                {(() => {
                  const entries = Object.entries(data.estimated_monthly).filter(([k]) => k !== "total");
                  const used = new Set<string>();
                  const groups = COST_GROUPS.map((g) => {
                    const items = entries.filter(([k]) => g.keys(k));
                    items.forEach(([k]) => used.add(k));
                    return { title: g.title, items };
                  }).filter((g) => g.items.length > 0);
                  const rest = entries.filter(([k]) => !used.has(k));
                  if (rest.length > 0) groups.push({ title: "Other", items: rest });

                  const row = (k: string, v: number | string) => {
                    const zero = typeof v === "number" && v === 0;
                    return (
                      <div key={k} className="flex items-center justify-between py-1 border-b border-slate-800/40">
                        <span className="text-xs text-slate-400">{prettyKey(k)}</span>
                        <span className={`text-xs font-mono ${zero ? "text-slate-600" : "text-slate-300"}`}>
                          {zero ? "free" : `$${typeof v === "number" ? v.toFixed(2) : v}`}
                        </span>
                      </div>
                    );
                  };

                  return groups.map((g) => {
                    const subtotal = g.items.reduce((a, [, v]) => a + (typeof v === "number" ? v : 0), 0);
                    return (
                      <div key={g.title}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{g.title}</span>
                          <span className="text-[10px] font-mono text-slate-500">${subtotal.toFixed(2)}</span>
                        </div>
                        <div className="space-y-1">{g.items.map(([k, v]) => row(k, v))}</div>
                      </div>
                    );
                  });
                })()}
                <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                  <span className="text-sm font-semibold text-slate-200">Est. Total</span>
                  <span className="text-lg font-bold text-brand-blue font-mono">
                    ${data.estimated_monthly.total?.toFixed(0)}<span className="text-[10px] text-slate-500 font-normal">/mo</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* API & Service Usage */}
          {data.api_usage && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Zap size={16} className="text-purple-400" />
                <h3 className="text-sm font-semibold text-slate-300">API & Service Usage</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">Service</th>
                      <th className="text-left py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">Plan</th>
                      <th className="text-left py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">Limit</th>
                      <th className="text-right py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">Used (24h)</th>
                      <th className="text-right py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">Cost</th>
                      <th className="text-left py-2.5 px-3 text-slate-400 font-semibold uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.api_usage).map(([name, u]) => (
                      <tr key={name} className="border-b border-slate-800/50 hover:bg-slate-800/20">
                        <td className="py-2.5 px-3 text-slate-200 font-medium capitalize">{name.replace(/_/g, " ")}</td>
                        <td className="py-2.5 px-3">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                            u.plan === "Free" || u.plan.includes("Free") ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" :
                            "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
                          }`}>{u.plan}</span>
                        </td>
                        <td className="py-2.5 px-3 text-slate-400">{u.limit}</td>
                        <td className="py-2.5 px-3 text-right text-slate-300 font-mono">{u.used_24h}</td>
                        <td className="py-2.5 px-3 text-right font-mono text-slate-300">{u.cost > 0 ? `$${u.cost.toFixed(2)}` : "Free"}</td>
                        <td className="py-2.5 px-3 text-slate-500 text-[10px]">{u.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* News Sources */}
          {data.news_sources && data.news_sources.length > 0 && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Newspaper size={16} className="text-yellow-400" />
                  <h3 className="text-sm font-semibold text-slate-300">News Sources</h3>
                </div>
                <span className="text-[10px] text-slate-500">{data.news_sources.length} sources registered</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 text-slate-400 font-semibold uppercase tracking-wider">Source</th>
                      <th className="text-left py-2 px-3 text-slate-400 font-semibold uppercase tracking-wider">Type</th>
                      <th className="text-right py-2 px-3 text-slate-400 font-semibold uppercase tracking-wider">24h</th>
                      <th className="text-right py-2 px-3 text-slate-400 font-semibold uppercase tracking-wider">7d</th>
                      <th className="text-right py-2 px-3 text-slate-400 font-semibold uppercase tracking-wider">Total</th>
                      <th className="text-left py-2 px-3 text-slate-400 font-semibold uppercase tracking-wider">Latest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.news_sources.map(s => (
                      <tr key={s.slug} className="border-b border-slate-800/50 hover:bg-slate-800/20">
                        <td className="py-2 px-3">
                          <div className="text-slate-200 font-medium">{s.name}</div>
                          <div className="text-[10px] text-slate-600 truncate max-w-[200px]">{s.url}</div>
                        </td>
                        <td className="py-2 px-3">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                            s.type === "gnews" ? "bg-brand-blue/10 text-brand-blue border-brand-blue/20" :
                            s.type === "rss" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" :
                            "bg-slate-500/10 text-slate-400 border-slate-500/20"
                          }`}>{s.type}</span>
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-slate-300">{s.articles_24h}</td>
                        <td className="py-2 px-3 text-right font-mono text-slate-300">{s.articles_7d}</td>
                        <td className="py-2 px-3 text-right font-mono text-slate-400">{s.total_articles.toLocaleString()}</td>
                        <td className="py-2 px-3 text-slate-500 text-[10px]">
                          {s.latest_article ? new Date(s.latest_article).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short" }) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* External links */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">External Dashboards</h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "GCP Billing Console", url: "https://console.cloud.google.com/billing/014A15-DE812B-86FF04/reports" },
                { label: "Cloud Run Dashboard", url: "https://console.cloud.google.com/run?project=tomitra-prod" },
                { label: "Cloud SQL Instances", url: "https://console.cloud.google.com/sql/instances?project=tomitra-prod" },
                { label: "Google AI Studio", url: "https://aistudio.google.com/apikey" },
                { label: "Firebase Console", url: "https://console.firebase.google.com/project/tomitra-prod" },
                { label: "Artifact Registry", url: "https://console.cloud.google.com/artifacts?project=tomitra-prod" },
              ].map(l => (
                <a key={l.label} href={l.url} target="_blank" rel="noopener"
                  className="flex items-center gap-2 text-xs text-brand-blue hover:underline bg-slate-800/30 rounded-lg p-3">
                  <ExternalLink size={12} /> {l.label}
                </a>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, sub, color }: { icon: React.ReactNode; label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider mb-2">{icon} {label}</div>
      <div className={`text-xl font-semibold ${color ?? "text-slate-200"}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
